import { useEffect, useRef, useState } from "react";
import { preload } from "react-dom";
import {
  cityOfThirstLoop,
  knapp1Crop as knapp1,
  knapp2Crop as knapp2,
  knapp3Crop as knapp3,
  knapp4Crop as knapp4,
  knapp5Crop as knapp5,
  knapp6Crop as knapp6,
  knapp8Crop as knapp8,
  knapp9Crop as knapp9,
  knappSoundcloud,
  popUpNewBackground as POPUP_BACKGROUND,
  riverSitterPaTv as RIDER_IDLE,
  riverSitterPaTvn2 as RIDER_PLAYING,
  tvPausNy as tvPaus,
  tvPlayNy as tvPlay,
  xRodClose,
} from "../assets";
import "./CityPopup.css";

type Service = {
  id: string;
  src: string;
  alt: string;
  service: string;
  /** Where the button goes. Omitted while the destination is still unknown —
   *  such an entry renders as the bare `<img>` it has always been. */
  href?: string;
  /** Ticket 042. Used instead of `href` when the pointer says desktop. Only
   *  Spotify sets one; everything else goes to the same place either way. */
  desktopHref?: string;
};

/**
 * The nine buttons under the TV, in the order they are stacked.
 *
 * Ticket 036: a release only gets an href once its destination actually exists.
 * City of Thirst went out on 2026-08-19 and its Feature.fm smart link flipped
 * from a pre-save page to real per-service destinations. Amazon Music was never
 * on that page; its URL came from the owner's own session instead (ticket 040).
 * Paradise (ticket 049) has no Amazon Music or Qobuz URL yet, so those two
 * render bare until one is found.
 *
 * URLs are stored canonically, the way `SocialIcons.tsx` requires: no `utm_*`,
 * `fbclid` or Spotify `si=`. YouTube's is the `watch?v=` form rather than the
 * `youtu.be` short link, which redirects to it and picks up a `feature=` tail on
 * the way.
 */
const SERVICES: Service[] = [
  {
    id: "city-btn-spotify",
    src: knapp1,
    alt: "Spotify",
    service: "spotify",
    // Ticket 042, the one button that goes to two places. Measured 2026-08-19:
    // the mobile app opens a /track/ link and *plays* it, but opens an album
    // page and waits. Desktop is the other way round — /track/ is a bare
    // one-song page, while the album with `?highlight=` lands on the EP with
    // this track selected, which is what Apple Music's `?i=` does. No single
    // URL does both, so `href` stays the mobile one and desktop opts in below.
    href: "https://open.spotify.com/track/2GrfU0Wr9TzToQFI74FsgA",
    desktopHref:
      "https://open.spotify.com/album/1yC6nMwwuubisyrFNOWXxN?highlight=spotify:track:2GrfU0Wr9TzToQFI74FsgA",
  },
  {
    id: "city-btn-apple-music",
    src: knapp2,
    alt: "Apple Music",
    service: "apple-music",
    // `geo.` is Apple's own storefront redirector: the country in the path is
    // inert, every visitor lands in their own store. A plain
    // `music.apple.com/se/` link would show a Swedish store to everyone.
    href: "https://geo.music.apple.com/us/album/paradise-single/6788505278?i=6788505282",
  },
  {
    id: "city-btn-youtube",
    src: knapp3,
    alt: "YouTube",
    service: "youtube",
    // The official video on RIVER's own channel, not the smart link's
    // `w2a1l300Xto` — that is the "RIVER - Topic" art track, which is what the
    // YouTube Music button below points at.
    href: "https://www.youtube.com/watch?v=aUzzjOuue4A",
  },
  {
    id: "city-btn-tidal",
    src: knapp4,
    alt: "Tidal",
    service: "tidal",
    href: "https://tidal.com/album/541106087",
  },
  {
    id: "city-btn-amazon-music",
    src: knapp5,
    alt: "Amazon Music",
    service: "amazon-music",
    // Ticket 049: no Paradise URL found yet. Share links arrive with
    // `marketplaceId=`, `musicTerritory=`, `ref=dm_sh_…` and `trackAsin=`;
    // the bare /tracks/<ASIN> path is what survives stripping them.
  },
  {
    id: "city-btn-youtube-music",
    src: knapp6,
    alt: "YouTube Music",
    service: "youtube-music",
    href: "https://music.youtube.com/watch?v=w2a1l300Xto",
  },
  {
    id: "city-btn-soundcloud",
    src: knappSoundcloud,
    alt: "SoundCloud",
    service: "soundcloud",
    href: "https://soundcloud.com/riverbrtejdm/paradise",
  },
  {
    id: "city-btn-deezer",
    src: knapp8,
    alt: "Deezer",
    service: "deezer",
    href: "https://www.deezer.com/track/4139288081",
  },
  {
    id: "city-btn-qobuz",
    src: knapp9,
    alt: "Qobuz",
    service: "qobuz",
    // Ticket 049: no Paradise URL found yet.
  },
];

export type { Service };
export { SERVICES };

/**
 * Call `onReady` once `src` has decoded — synchronously if it already has.
 * A load failure counts as ready: whatever waits on the image must not be left
 * hidden because it never arrived. Returns a cleanup that drops the callback.
 *
 * `ImageCtor` is injectable so the three paths can be tested without a DOM.
 */
export function whenImageReady(
  src: string,
  onReady: () => void,
  ImageCtor: typeof Image | undefined = typeof Image === "undefined" ? undefined : Image,
) {
  if (!ImageCtor) return () => {};
  const img = new ImageCtor();
  img.src = src;
  if (img.complete) {
    onReady();
    return () => {};
  }
  let live = true;
  const done = () => {
    if (live) onReady();
  };
  img.addEventListener("load", done);
  img.addEventListener("error", done);
  return () => {
    live = false;
    img.removeEventListener("load", done);
    img.removeEventListener("error", done);
  };
}

/**
 * Ticket 042: which Spotify URL the button carries. The real question is not
 * "big screen?" but **"will an https link hand off to a native app?"** — iOS,
 * iPadOS and Android intercept `open.spotify.com` with universal links and open
 * the Spotify app; desktop browsers cannot, and always land in the web player.
 *
 * Hence the touch check, which does the heavy lifting. A pointer test alone
 * fails on an iPad with a Magic Keyboard: iPadOS reports `hover: hover` and
 * `pointer: fine` once a trackpad is attached, so the iPad would be handed the
 * desktop URL and open it in the Spotify app, which does not autoplay an album.
 * `maxTouchPoints` catches it — iPadOS claims to be a Mac in its user-agent but
 * still reports 5 touch points, which is the standard way to tell them apart.
 *
 * It is allowed to fail in exactly one direction. Anything unrecognised, and
 * server render, reads as *not* desktop, because the mobile link is the one
 * already behaving correctly and a wrong guess there costs autoplay. The cost is
 * that a touchscreen Windows laptop gets the mobile link: no app to hand off to,
 * so it keeps the plain track page instead of the EP. Nothing breaks, it is just
 * the pre-042 behaviour.
 *
 * Both inputs are injectable for the tests.
 */
export function isDesktopBrowser(
  mm: typeof matchMedia | undefined = typeof matchMedia === "undefined" ? undefined : matchMedia,
  touchPoints: number = typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints,
) {
  if (touchPoints > 0) return false;
  return mm?.("(hover: hover) and (pointer: fine)").matches ?? false;
}

/**
 * One button under the TV: an anchor when the release has a destination, the
 * bare `<img>` it has always been when it does not.
 *
 * The identity goes on the outer element either way: `#city-btn-*` carries the
 * offsets and `.city-service-btn` carries both `position: absolute` and the
 * `bg-ready` opacity gate, so on a linked button they belong to the anchor and
 * the image just fills it. See `a.city-service-btn img` in CityPopup.css.
 */
export function ServiceButton({
  service: { id, src, alt, service, href, desktopHref },
  onNavigate,
  desktop = false,
}: {
  service: Service;
  onNavigate?: () => void;
  desktop?: boolean;
}) {
  // `href` is the fallback on purpose — see `isDesktopPointer`.
  const target = desktop ? (desktopHref ?? href) : href;
  if (!target) {
    return <img className="city-service-btn" id={id} src={src} alt={alt} data-service={service} />;
  }
  return (
    <a
      className="city-service-btn"
      id={id}
      href={target}
      data-service={service}
      // Ticket 041: the new tab starts the same song, so hand the loop off
      // rather than leaving it playing in the tab behind them.
      onClick={onNavigate}
      // Ticket 013: navigating the tab away tears down the track that is
      // playing in this very popup.
      target="_blank"
      rel="noopener noreferrer"
    >
      <img src={src} alt={alt} />
    </a>
  );
}

function formatTime(sec: number) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

/** The part of `HTMLAudioElement` the popup's controls touch, so a test can fake it. */
export type PopupTrack = {
  play(): void;
  pause(): void;
};

/**
 * The popup's audio actions, with no React in them: the element arrives through
 * `getTrack` (a ref read, in the component) and the three pieces of state go out
 * through `set`. Kept separate for the same reason `createLoopControls` is —
 * the ducking rules are where the bugs are, and there is no DOM in the tests.
 */
export function createPopupAudio(
  getTrack: () => PopupTrack | null,
  set: {
    playing: (v: boolean) => void;
    elapsed: (v: number | null) => void;
    progress: (v: number) => void;
  },
  duck: () => void,
  unduck: () => void,
) {
  const stop = () => {
    set.playing(false);
    getTrack()?.pause();
    unduck();
  };
  const clearReadout = () => {
    set.elapsed(null);
    set.progress(0);
  };

  return {
    stop,
    toggle(playing: boolean, elapsed: number | null) {
      if (playing) return stop();
      set.playing(true);
      if (elapsed === null) set.elapsed(0);
      duck();
      getTrack()?.play();
    },

    /**
     * Ticket 041. Deliberately not `stop()`: that unducks, and the visitor is on
     * their way to a service in a new tab — resuming the site's background loop
     * here would just put a second song under the one they left to hear. The
     * loop stays ducked until the popup closes.
     */
    pauseForOutbound() {
      set.playing(false);
      getTrack()?.pause();
    },

    close(playing: boolean) {
      // Unconditional, because `pauseForOutbound` leaves `playing` false while
      // the loop is still ducked — inside the `if` below this would never run
      // and the site would stay silent for the rest of the visit. `unduck` is a
      // no-op when nothing is ducked, so every other path is unchanged.
      unduck();
      if (playing) {
        stop();
        clearReadout();
      }
    },

    ended() {
      stop();
      clearReadout();
    },
  };
}

/**
 * "City of Thirst — out now": a poster with a playable TV and links out to
 * every streaming service. Opened from the lips and the speech bubble.
 */
export function CityPopup({
  open,
  onClose,
  duck,
  unduck,
}: {
  open: boolean;
  onClose: () => void;
  duck: () => void;
  unduck: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  /** null once the track has been stopped/ended and the readout is cleared */
  const [elapsed, setElapsed] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  // Ticket 042: read once on mount rather than per render. Nobody swaps device
  // mid-visit, and this way the markup cannot flicker.
  const [desktop] = useState(isDesktopBrowser);

  // The box paints its backdrop as a CSS background-image, which the browser
  // will not fetch while the overlay is display:none — left alone it starts
  // downloading on the click that opens the popup, so the buttons (plain
  // <img>, loaded with the page) paint first and float on black. Preloading it
  // during render moves that fetch to page load, and the buttons wait on its
  // real load rather than on a guessed delay.
  preload(POPUP_BACKGROUND, { as: "image" });
  const [bgReady, setBgReady] = useState(false);
  useEffect(() => whenImageReady(POPUP_BACKGROUND, () => setBgReady(true)), []);

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      const audio = audioRef.current;
      if (!audio) return;
      setElapsed(audio.currentTime);
      if (audio.duration) setProgress((audio.currentTime / audio.duration) * 100);
    }, 250);
    return () => clearInterval(id);
  }, [playing]);

  const audio = createPopupAudio(
    () => audioRef.current,
    { playing: setPlaying, elapsed: setElapsed, progress: setProgress },
    duck,
    unduck,
  );

  function close() {
    onClose();
    audio.close(playing);
  }

  return (
    <div
      id="city-popup-overlay"
      className={open ? "open" : undefined}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        id="city-popup-box"
        className={bgReady ? "bg-ready" : undefined}
        style={{ backgroundImage: `url(${POPUP_BACKGROUND})` }}
      >
        <button type="button" id="city-popup-close-btn" onClick={close}>
          <img src={xRodClose} alt="Stäng" />
        </button>
        <img
          id="city-tv"
          src={playing ? tvPaus : tvPlay}
          alt=""
          onClick={() => audio.toggle(playing, elapsed)}
        />
        <img id="city-tv-rider" src={playing ? RIDER_PLAYING : RIDER_IDLE} alt="" />
        <div id="city-tv-progress-track" className={elapsed !== null ? "show" : undefined}>
          <div id="city-tv-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div id="city-tv-timer" className={elapsed !== null ? "show" : undefined}>
          {formatTime(elapsed ?? 0)}
        </div>
        <audio ref={audioRef} preload="auto" onEnded={audio.ended}>
          {/* Ogg Opus only — browsers without it (Safari < 17) are out of scope. */}
          <source src={cityOfThirstLoop} type="audio/ogg; codecs=opus" />
        </audio>
        {SERVICES.map((service) => (
          <ServiceButton
            key={service.id}
            service={service}
            onNavigate={audio.pauseForOutbound}
            desktop={desktop}
          />
        ))}
      </div>
    </div>
  );
}
